/**
 * 開発者コントロールパネル（開発環境専用）
 * モックAPI切り替えとフィーチャーフラグを統合した開発者向けコントロールパネル
 */

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FeatureFlag, featureFlagManager } from '@src/config/featureFlags';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useMockModeContext } from './MockModeProvider';

const FEATURE_FLAGS: { key: FeatureFlag; label: string }[] = [
  { key: FeatureFlag.USE_RIREKISHO, label: '履歴書' },
  { key: FeatureFlag.USE_RESUME, label: 'レジュメ' },
  { key: FeatureFlag.AUTH_ENABLED, label: '認証' },
  { key: FeatureFlag.FOCUS_MODE, label: 'フォーカスモード' },
  { key: FeatureFlag.AI_REWRITE, label: 'AIリライト' },
  { key: FeatureFlag.TYPO_FIX, label: 'タイポ修正' },
];

export default function DevControlPanel(): React.ReactElement | null {
  const [flags, setFlags] = useState<Record<FeatureFlag, boolean>>(() => {
    const initialFlags: Record<string, boolean> = {};
    for (const flag of Object.values(FeatureFlag)) {
      initialFlags[flag] = featureFlagManager.isEnabled(flag);
    }
    return initialFlags as Record<FeatureFlag, boolean>;
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  const context = useMockModeContext();
  const isMockMode = context?.isMockMode ?? false;
  const apiConfig = context?.apiConfig ?? { baseUrl: 'Unknown API' };
  const toggleMockMode =
    context?.toggleMockMode ??
    (() => {
      console.warn('DevControlPanel: Context not available, toggle not functional');
    });
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const surfaceColor = isDark ? 'rgba(18, 18, 18, 0.92)' : 'rgba(255, 255, 255, 0.85)';
  const surfaceHoverColor = isDark ? 'rgba(28, 28, 28, 0.96)' : 'rgba(255, 255, 255, 0.95)';
  const borderColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'divider';
  const shadow = isDark
    ? '0 12px 40px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.5)'
    : '0 8px 32px rgba(0, 0, 0, 0.12)';

  const handleFlagToggle = useCallback(
    (key: FeatureFlag) => {
      const newValue = !flags[key];
      featureFlagManager.setFlag(key, newValue);
      setFlags((prev) => ({ ...prev, [key]: newValue }));

      window.dispatchEvent(
        new CustomEvent('feature-flag-changed', {
          detail: { key, value: newValue },
        })
      );
    },
    [flags]
  );

  const handleResetFlags = useCallback(() => {
    const defaultFlags: Record<FeatureFlag, boolean> = {
      [FeatureFlag.USE_RIREKISHO]: false,
      [FeatureFlag.USE_RESUME]: true,
      [FeatureFlag.AUTH_ENABLED]: false,
      [FeatureFlag.FOCUS_MODE]: false,
      [FeatureFlag.AI_REWRITE]: false,
      [FeatureFlag.TYPO_FIX]: false,
    };

    for (const [key, value] of Object.entries(defaultFlags)) {
      featureFlagManager.setFlag(key as FeatureFlag, value);
    }

    setFlags(defaultFlags);
  }, []);

  useEffect(() => {
    const unsubscribe = featureFlagManager.subscribe((newFlags) => {
      setFlags(newFlags);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const handleMockModeChange = (_event: CustomEvent) => {};

    window.addEventListener('mock-mode-changed', handleMockModeChange as EventListener);

    return () => {
      window.removeEventListener('mock-mode-changed', handleMockModeChange as EventListener);
    };
  }, []);

  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 68,
        right: 16,
        p: 0.75,
        minWidth: 160,
        maxWidth: 200,
        zIndex: 1200,
        border: '1px solid',
        borderColor,
        borderRadius: 1.5,
        backgroundColor: surfaceColor,
        backdropFilter: 'blur(10px)',
        opacity: isExpanded ? 1 : 0.25,
        transform: isExpanded ? 'scale(1)' : 'scale(0.95)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          opacity: 1,
          transform: 'scale(1)',
          backgroundColor: surfaceHoverColor,
          boxShadow: shadow,
        },
      }}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <Stack spacing={0.75}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" fontWeight="bold" fontSize="0.7rem">
            開発者
          </Typography>
          <Chip
            label={isMockMode ? 'MOCK' : 'API'}
            color={isMockMode ? 'warning' : 'primary'}
            size="small"
            variant="filled"
            sx={{
              height: 16,
              fontSize: '0.55rem',
              '& .MuiChip-label': {
                px: 0.5,
              },
            }}
          />
        </Box>

        <Alert
          severity={isMockMode ? 'warning' : 'info'}
          sx={{
            py: 0.2,
            px: 0.5,
            fontSize: '0.55rem',
            backgroundColor: (alertTheme) =>
              isMockMode
                ? alpha(alertTheme.palette.warning.main, isDark ? 0.16 : 0.12)
                : alpha(alertTheme.palette.info.main, isDark ? 0.16 : 0.12),
            border: '1px solid',
            borderColor: (alertTheme) =>
              isMockMode
                ? alpha(alertTheme.palette.warning.main, isDark ? 0.35 : 0.2)
                : alpha(alertTheme.palette.info.main, isDark ? 0.35 : 0.2),
            color: (alertTheme) => alertTheme.palette.text.primary,
            '& .MuiAlert-icon': {
              fontSize: '0.75rem',
            },
          }}
        >
          <Typography
            variant="caption"
            fontSize="0.55rem"
            sx={(theme) => ({ color: theme.palette.text.primary })}
          >
            {apiConfig.baseUrl.replace('http://', '').replace('https://', '')}
          </Typography>
        </Alert>

        <Button
          variant="outlined"
          size="small"
          onClick={toggleMockMode}
          color={isMockMode ? 'primary' : 'warning'}
          fullWidth
          sx={{
            fontSize: '0.6rem',
            py: 0.2,
            px: 0.75,
            minHeight: 20,
            textTransform: 'none',
            lineHeight: 1.2,
          }}
        >
          {isMockMode ? 'API切替' : 'モック切替'}
        </Button>

        <Divider sx={{ my: 0.25 }} />

        <Accordion
          expanded={isAccordionExpanded}
          onChange={() => setIsAccordionExpanded(!isAccordionExpanded)}
          sx={{
            backgroundColor: 'transparent',
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            '& .MuiAccordion-region': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ fontSize: '0.85rem' }} />}
            sx={{
              minHeight: 24,
              padding: '0 4px',
              '& .MuiAccordionSummary-content': {
                margin: '4px 0',
              },
              '&.Mui-expanded': {
                minHeight: 24,
              },
            }}
          >
            <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">
              フラグ
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: '4px 4px 2px' }}>
            <Stack spacing={0.5}>
              <Stack spacing={0.25}>
                {FEATURE_FLAGS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        size="small"
                        checked={flags[key]}
                        onChange={() => handleFlagToggle(key)}
                        sx={{
                          '& .MuiSwitch-switchBase': {
                            padding: '6px',
                          },
                          '& .MuiSwitch-track': {
                            height: 12,
                          },
                          '& .MuiSwitch-thumb': {
                            width: 10,
                            height: 10,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" fontSize="0.65rem">
                        {label}
                      </Typography>
                    }
                    sx={{
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        marginLeft: 0.25,
                      },
                    }}
                  />
                ))}
              </Stack>

              <Button
                variant="text"
                size="small"
                onClick={handleResetFlags}
                color="inherit"
                fullWidth
                sx={{
                  fontSize: '0.6rem',
                  py: 0.2,
                  textTransform: 'none',
                  minHeight: 20,
                }}
              >
                リセット
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Paper>
  );
}
